/**
 * Venda parcelada (chamado 2026-0067): carregar as n parcelas de uma venda a partir de qualquer
 * uma delas, e salvar a venda inteira numa chamada (POST /recebimentos/salvar_parcelas, uma
 * transação no backend). A tela é RecebimentoParceladoModal.
 */
import { ref } from 'vue'
import { httpService } from '../services/httpService'
import { log } from '../utils/DebugLogger'
import { useRecebimento } from './useRecebimento'
import { chaveDoGrupo, dataIso, type LinhaDeRecebimento, type MudancaDeParcela } from './useParcelamento'
import '../contracts/Recebimentos_SalvarParcelasInput'
import '../contracts/Recebimentos_SalvarParcelasResponse'
import type { Recebimentos_SalvarParcelasResponseDados } from '../contracts/Recebimentos_SalvarParcelasResponse'

/** Uma linha de recebimento como as listas devolvem (o que a venda parcelada lê). */
export interface LinhaDaVenda extends LinhaDeRecebimento {
  recebimento_id: number
  recebimento_origem?: string
  recebimento_origem_id?: number
  recebimento_forma_pagamento_id?: number
  recebimento_data?: string
  recebimento_valor?: number
  recebimento_valor_taxas?: number
  recebimento_observacoes?: string
  recebimento_provedor?: string
  [key: string]: unknown
}

/** O que é da VENDA e vale para todas as parcelas. */
export interface VendaEditavel {
  recebimento_forma_pagamento_id: number | null
  /** YYYY-MM-DD — o dia em que o cliente pagou */
  recebimento_data: string
  recebimento_observacoes: string
}

/** O payload do salvar_parcelas: campos da venda + só o que mudou em cada parcela. */
export interface SalvarVendaParcelada {
  recebimento_id: number
  recebimento_data?: string
  recebimento_forma_pagamento_id?: number
  recebimento_observacoes?: string
  parcelas: MudancaDeParcela[]
}

export function useVendaParcelada() {
  const { fetchRecebimento, fetchRecebimentosByOrigem } = useRecebimento()
  const isLoading = ref(false)

  /** As parcelas da venda de `recebimentoId` (qualquer parcela), na ordem das parcelas. */
  const carregar = async (recebimentoId: number): Promise<LinhaDaVenda[]> => {
    isLoading.value = true
    try {
      const atual = (await fetchRecebimento(recebimentoId)) as LinhaDaVenda
      const chave = chaveDoGrupo(atual)
      const daOrigem = (await fetchRecebimentosByOrigem(String(atual.recebimento_origem), Number(atual.recebimento_origem_id))) as unknown as LinhaDaVenda[]
      // As datas da lista vêm em DD/MM/YYYY (Record); a tela edita e compara em YYYY-MM-DD.
      const venda = daOrigem
        .filter((l) => chaveDoGrupo(l) === chave)
        .map((l) => ({ ...l, recebimento_data: dataIso(l.recebimento_data), recebimento_data_disponivel: dataIso(l.recebimento_data_disponivel) }))
        .sort((a, b) => Number(a.recebimento_parcela_numero ?? 1) - Number(b.recebimento_parcela_numero ?? 1))
      log('useVendaParcelada', 'carregar', 'Venda carregada', { recebimentoId, chave, parcelas: venda.length })
      return venda.length > 0 ? venda : [atual]
    } finally {
      isLoading.value = false
    }
  }

  /** Salva a venda inteira; a recusa do backend chega como Error(código). */
  const salvar = async (payload: SalvarVendaParcelada): Promise<Recebimentos_SalvarParcelasResponseDados> => {
    isLoading.value = true
    try {
      const response = await httpService.post('/index.php/recebimentos/salvar_parcelas', payload, {
        inputContract: 'Recebimentos_SalvarParcelasInput',
        responseContract: 'Recebimentos_SalvarParcelasResponse',
      })
      if (!response.success) throw new Error(response.error || 'Erro ao salvar a venda parcelada')
      return response.dados as Recebimentos_SalvarParcelasResponseDados
    } finally {
      isLoading.value = false
    }
  }

  return { isLoading, carregar, salvar }
}
